import * as React from 'react';
import { useActions as useSchemaModalActions } from '../SchemaModal/schemaModalActions';
import { useActions as useNavActions } from './navActions';
import './nav.scss';
import { cn, isEnterpriseSchema, isSystemSchema } from '../../utils';
import { Tabular, Workspace } from '../Icon/icon';
import { Arrow, Edit, Icon, Trash } from 'rcd-cpp-ui/ui/icon';
import { Dropbox } from '../Dropbox/dropbox';
import { Schema } from '../../types/Metadata';
import { NavGroup } from './NavGroup/navGroup';
import { ConfirmModal } from '../ConfirmModal/confirmModal';
import { NavView } from './NavView/navView';
import AccessControl from '../AccessControl/accessControl';
import { useResizer } from '../Resizer/resizer';
import { NavAdmin } from './NavAdmin/navAdmin';
import { NavGroups } from '../../enums/NavGroups';
import { WorkspaceList } from './workspaceList';
import { Resource } from '../../enums/Resource';
import { WYSIWYGEditor } from '../WYSIWYGEditor/WYSIWYGEditor';
import { Modal } from 'rcd-cpp-ui/ui/modal/Modal';
import { useSelector } from '../../utils/useSelector';
import { AppRefId } from '../../utils/constants';
import { useLoader } from '../Loader/loader';
import { useAlert } from '../Alert/alert';
import { useFullStory } from '../App/fullStory';
import { TriggerLocation } from '../../enums/TriggerLocation';
import { FSEvent } from '../../enums/FSEvent';
import { NewIdeaMenu } from '../NewIdeaMenu/newIdeaMenu';

enum OptionName {
  CreateCustomData = 'Create New Data Type',
  Edit = 'Edit',
  Delete = 'Delete'
}
type Option = { name: OptionName; Icon: any };

export const Nav = () => {
  const workspaces = useSelector(s => s.planning?.workspaces);
  const selectedWorkspace = useSelector(s => s.planning?.selectedWorkspace);
  const selectedViewId = useSelector(s => s.view?.selected?.id);
  const homeWorkspaceId = useSelector(s => {
    if (!s.planning?.userSettings) return null;
    const userSetting = s.planning.userSettings[AppRefId];
    return userSetting?.props?.homeWorkspaceId;
  });
  const workspaceInformation =
    selectedWorkspace?.setting?.props?.workspaceInformation;
  const demandSensingResources =
    selectedWorkspace?.setting?.props?.demandSensingResources;
  const [showModal, setShowModal] = React.useState(false);
  const [showDSRModal, setShowDSRModal] = React.useState(false);
  const { Resizer, resizerWidth, resizerRef } = useResizer({ minWidth: 240 });
  const { openSchemaModal, deleteSchema } = useSchemaModalActions();
  const { navigate } = useNavActions();
  const { captureEvent } = useFullStory();
  const [enterpricesDomains, customDomains, managedDomains] = (
    selectedWorkspace?.schemas || []
  ).reduce(
    (p, c, idx, arr) => {
      const [eDomains, cDomains, mDomains] = p;
      if (c.props.isManaged) {
        mDomains.push(c);
      } else if (!c.props?.excludeFromEnterpriseDataList) {
        if (isEnterpriseSchema(c)) eDomains.push(c);
        else cDomains.push(c);
      }
      if (idx == arr.length - 1) {
        eDomains.sort((a, b) => a.props.name.localeCompare(b.props.name));
        cDomains.sort((a, b) => a.props.name.localeCompare(b.props.name));
        mDomains.sort((a, b) => a.props.name.localeCompare(b.props.name));
      }
      return p;
    },
    [[], [], []] as Schema[][]
  );
  const [expanded, setExpanded] = React.useState(true);
  const [schemaToDelete, setSchemaToDelete] = React.useState<Schema>(null);

  const getSchemaOptions = React.useCallback(
    (s: Schema) => {
      if (s.isInherited && !isSystemSchema(s)) {
        return [];
      }
      const options: Option[] = [];
      if (isSystemSchema(s)) {
        if (s.props.allowCustomFields) {
          options.push({ name: OptionName.Edit, Icon: Edit });
        }
      } else if (!s.baseSchema) {
        options.push({ name: OptionName.Edit, Icon: Edit });
      }
      if (!isSystemSchema(s)) {
        options.push({ name: OptionName.Delete, Icon: Trash });
      }
      return options;
    },
    [selectedWorkspace]
  );
  const handleSelectSchemaOption = React.useCallback((o: Option, s: Schema) => {
    if (o.name == OptionName.Edit) openSchemaModal(s);
    if (o.name == OptionName.Delete && !isSystemSchema(s)) setSchemaToDelete(s);
  }, []);
  const handleAddCustomSchema = React.useMemo(() => {
    if (!selectedWorkspace?.props?.allowOwnCustomData) return null;
    return () => openSchemaModal();
  }, [selectedWorkspace]);

  if (!workspaces) return <div className="cp-nav" />;
  const isHomeWorkspace =
    !!homeWorkspaceId && selectedWorkspace?.props?.refId == homeWorkspaceId;
  return (
    <>
      <div
        className={cn(
          { 'cp-nav-collapsed': !expanded, 'cpp-nav-home-ws': isHomeWorkspace },
          'cp-nav'
        )}
        id={'navFlyout'}
        ref={resizerRef}
        style={{ width: resizerWidth }}
      >
        <div
          className={`ws-dropdown-link-container ${!expanded ? 'closed' : ''}`}
        >
          <div className="ws-dropdown">
            {expanded! && (
              <Dropbox
                childClassName={'cp-ws-container scrollable'}
                renderBox={closeDD => (
                  <WorkspaceList
                    workspaces={workspaces}
                    selectedWorkspace={selectedWorkspace}
                    navigate={navigate}
                    callback={closeDD}
                  />
                )}
              >
                <span>
                  <Workspace
                    isHome={isHomeWorkspace}
                    title={
                      isHomeWorkspace
                        ? 'This is your home workspace'
                        : 'This is not your home workspace'
                    }
                  />
                </span>
                <span
                  className="workspace-name cp-nav-collapsible"
                  title={selectedWorkspace?.props?.name}
                >
                  {selectedWorkspace
                    ? selectedWorkspace?.props?.name
                    : 'Select a Workspace'}
                </span>
                <span className="cp-nav-collapsible">
                  <Arrow direction="down" type="line" />
                </span>
              </Dropbox>
            )}
            <div className="arrow">
              <Icon
                onClick={() => {
                  setExpanded(!expanded);
                }}
              >
                <Arrow direction={expanded ? 'left' : 'right'} type="circle" />
              </Icon>
            </div>
          </div>
          {expanded! && (
            <div
              className="workspace-info-link"
              onClick={() => {
                setShowModal(true);
              }}
            >
              See Workspace Information
            </div>
          )}
          {expanded && !!demandSensingResources && (
            <div
              className="workspace-info-link"
              onClick={() => {
                setShowDSRModal(true);
                captureEvent(FSEvent.DemandSensingResources);
              }}
            >
              See Demand Sensing Resources
            </div>
          )}
        </div>
        {showModal && (
          <Modal
            id="workspace-modal"
            header={`${selectedWorkspace?.props?.name} Workspace Information`}
            onClose={() => setShowModal(false)}
          >
            <div
              className="workspace-info-link-modal"
              onClick={() => {
                setShowModal(false);
                navigate(selectedWorkspace, { path: '/admin/permission' });
              }}
            >
              See Workspace Contacts
            </div>
            <div className="workspace-info-description">
              <WYSIWYGEditor
                content={workspaceInformation}
                readonly={true}
                disabled={true}
                onChange={null}
              />
            </div>
          </Modal>
        )}
        {showDSRModal && (
          <Modal
            id="workspace-modal"
            header={`${selectedWorkspace?.props?.name} Demand Sensing Resources`}
            onClose={() => setShowDSRModal(false)}
          >
            <div className="workspace-info-description">
              <WYSIWYGEditor
                content={demandSensingResources}
                readonly={true}
                disabled={true}
                onChange={null}
                onClickLink={'captureDemandSensingResourcesLink(this.href)'}
              />
            </div>
          </Modal>
        )}
        {selectedWorkspace && expanded! && (
          <>
            <div className="cp-nav-collapsible">
              <AccessControl resource={Resource.addNewItem}>
                <NewIdeaMenu
                  triggerLocation={TriggerLocation.NAV}
                  position={'left'}
                />
              </AccessControl>
            </div>
            <div className="nav-groups scrollable" key={selectedWorkspace?.id}>
              <AccessControl resource={Resource.view}>
                <NavView />
              </AccessControl>
              <AccessControl resource={Resource.schema}>
                <NavGroup
                  name={NavGroups.CustomData}
                  onAdd={handleAddCustomSchema}
                  items={customDomains}
                  handleSelectItem={s => {
                    navigate(selectedWorkspace, { v: s.props.refId });
                  }}
                  selectedItemId={selectedViewId}
                  handleSelectOption={handleSelectSchemaOption}
                  getItemIcon={i => <Tabular />}
                  getItemOptions={getSchemaOptions}
                />
              </AccessControl>
              <AccessControl resource={Resource.schema}>
                <NavGroup
                  name={NavGroups.EnterpriseData}
                  items={enterpricesDomains}
                  handleSelectItem={s => {
                    navigate(selectedWorkspace, { v: s.props.refId });
                  }}
                  selectedItemId={selectedViewId}
                  getItemIcon={i => <Tabular />}
                  handleSelectOption={handleSelectSchemaOption}
                  getItemOptions={getSchemaOptions}
                />
              </AccessControl>
              {!!managedDomains.length && (
                <AccessControl resource={Resource.schema}>
                  <NavGroup
                    name={NavGroups.ManagedData}
                    items={managedDomains}
                    handleSelectItem={s => {
                      navigate(selectedWorkspace, { v: s.props.refId });
                    }}
                    selectedItemId={selectedViewId}
                    getItemIcon={i => <Tabular />}
                    handleSelectOption={handleSelectSchemaOption}
                    getItemOptions={getSchemaOptions}
                  />
                </AccessControl>
              )}
              <AccessControl resource={Resource.viewAdministration}>
                <NavAdmin />
              </AccessControl>
            </div>
          </>
        )}
      </div>
      {schemaToDelete && (
        <DeleteSchemaModal
          onCancel={() => setSchemaToDelete(null)}
          onDelete={async () => {
            await deleteSchema(schemaToDelete);
            setSchemaToDelete(null);
          }}
          schema={schemaToDelete}
        />
      )}
      {expanded! && <Resizer />}
    </>
  );
};

type DSMProps = {
  schema: Schema;
  onCancel: () => any;
  onDelete: () => Promise<any>;
};
const DeleteSchemaModal = ({ schema, onCancel, ...props }: DSMProps) => {
  const { onDelete, Loader, Alert } = useLoader(useAlert(props));
  return (
    <ConfirmModal
      header="Delete Custom Data Type"
      onCancel={onCancel}
      onDelete={onDelete}
    >
      <Loader />
      <Alert />
      <div>Are you sure you want to delete this Custom Data Type?</div>
      <div>
        Name: <b>{schema.props?.name}</b>
      </div>
    </ConfirmModal>
  );
};
